import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://steinsoft.com.br';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;

/**
 * PageSEO — aplica <title>, <meta>, Open Graph, Twitter Card e JSON-LD por página.
 *
 * Props:
 *   title       string  — título da página (sem sufixo "| SteinSoft")
 *   description string  — meta description da página
 *   path        string  — caminho relativo, ex: "/Elohim"
 *   image       string  — URL absoluta da og:image (opcional)
 *   lang        string  — idioma atual: "pt" | "en" | "es" (opcional)
 *   jsonLd      object  — schema JSON-LD adicional (opcional)
 */
export default function PageSEO({ title, description, path = '/', image, lang = 'pt', jsonLd }) {
  const fullTitle = title ? `${title} | SteinSoft` : 'SteinSoft — Desenvolvimento de Software e Consultoria em TI';
  const url = `${BASE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  const ogLocale = { pt: 'pt_BR', en: 'en_US', es: 'es_ES' }[lang] ?? 'pt_BR';

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SteinSoft',
    url: BASE_URL,
    logo: DEFAULT_IMAGE,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+55-48-98839-3447',
      contactType: 'customer service',
      availableLanguage: ['Portuguese', 'English', 'Spanish'],
    },
    sameAs: [
      'https://www.instagram.com/steinsoftt',
      'https://www.linkedin.com/company/steinsoft',
    ],
  };

  const pageSchema = jsonLd ?? {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: fullTitle,
    description,
    url,
    isPartOf: { '@type': 'WebSite', url: BASE_URL, name: 'SteinSoft' },
  };

  return (
    <Helmet>
      {/* Basics */}
      <html lang={lang === 'pt' ? 'pt-BR' : lang === 'es' ? 'es' : 'en'} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* hreflang — mesmo URL, idioma via localStorage */}
      <link rel="alternate" hrefLang="pt-BR" href={url} />
      <link rel="alternate" hrefLang="en"    href={url} />
      <link rel="alternate" hrefLang="es"    href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type"        content="website" />
      <meta property="og:url"         content={url} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:locale"      content={ogLocale} />
      <meta property="og:site_name"   content="SteinSoft" />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />

      {/* JSON-LD: Organização */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      {/* JSON-LD: Página */}
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
    </Helmet>
  );
}
